import React, { useRef } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  Avatar,
  useColorModeValue,
  Icon,
  IconButton,
  AspectRatio,
  Image,
  Tooltip,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import type { CordeCommonModelsDtoTeamTeam } from '@/api/generated/v1/api';

// Icons
const BackIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </Icon>
);

const EditIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </Icon>
);

const ViewIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path fill="currentColor" d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
  </Icon>
);

const DeleteIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </Icon>
);

interface TeamHeaderProps {
  team: CordeCommonModelsDtoTeamTeam;
  permissions: {
    manage_team_settings: boolean;
    create_team_avatar: boolean;
    delete_team_avatar: boolean;
    create_team_banner: boolean;
    delete_team_banner: boolean;
  };
  getAvatarUrl: () => string | undefined;
  getBannerUrl: () => string | undefined;
  isLoadingAvatarBanner: boolean;
  onAvatarUpload: (file: File) => void;
  onBannerUpload: (file: File) => void;
  onAvatarDelete: () => void;
  onBannerDelete: () => void;
}

export const TeamHeader: React.FC<TeamHeaderProps> = ({
  team,
  permissions,
  getAvatarUrl,
  getBannerUrl,
  isLoadingAvatarBanner,
  onAvatarUpload,
  onBannerUpload,
  onAvatarDelete,
  onBannerDelete,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  
  const textColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <VStack spacing={8} align="stretch">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/teams">
            {t('teams.title')}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>{team.name}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Header with Banner and Avatar */}
      <Card shadow="lg" overflow="hidden">
        {/* Team Banner */}
        <Box position="relative">
          <AspectRatio ratio={16 / 6} maxH="200px">
            <Box
              bg={useColorModeValue('gradient.blue', 'gradient.purple')}
              bgGradient={useColorModeValue(
                'linear(to-r, blue.400, purple.500)',
                'linear(to-r, blue.600, purple.700)'
              )}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {getBannerUrl() ? (
                <Image
                  src={getBannerUrl()}
                  alt="Team Banner"
                  objectFit="cover"
                  w="full"
                  h="full"
                />
              ) : (
                <VStack spacing={2} color="white" opacity={0.7}>
                  <Text fontSize="lg" fontWeight="medium">
                    {team.name}
                  </Text>
                  <Text fontSize="sm">Team Banner</Text>
                </VStack>
              )}
            </Box>
          </AspectRatio>

          {/* Banner Management Overlay */}
          {permissions.manage_team_settings && (
            <Box
              position="absolute"
              top={2}
              right={2}
              bg={useColorModeValue('blackAlpha.600', 'blackAlpha.800')}
              borderRadius="md"
              p={1}
            >
              <HStack spacing={1}>
                {getBannerUrl() && (
                  <Tooltip label="View Banner">
                    <IconButton
                      icon={<ViewIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="white"
                      onClick={() => {}}
                      aria-label="View Banner"
                    />
                  </Tooltip>
                )}
                <Tooltip label="Upload Banner">
                  <IconButton
                    icon={<EditIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="white"
                    onClick={() => bannerInputRef.current?.click()}
                    isLoading={isLoadingAvatarBanner}
                    aria-label="Upload Banner"
                  />
                </Tooltip>
                {getBannerUrl() && (
                  <Tooltip label="Delete Banner">
                    <IconButton
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={onBannerDelete}
                      isLoading={isLoadingAvatarBanner}
                      aria-label="Delete Banner"
                    />
                  </Tooltip>
                )}
              </HStack>
            </Box>
          )}
        </Box>

        {/* Team Info with Avatar */}
        <CardBody>
          <VStack spacing={4} align="stretch">
            {/* Avatar and Basic Info Row */}
            <HStack spacing={4} align="start">
              {/* Team Avatar */}
              <Box position="relative">
                <Avatar
                  size="2xl"
                  name={team.name}
                  src={getAvatarUrl()}
                  bg={useColorModeValue('blue.500', 'blue.400')}
                  color="white"
                  border="4px solid"
                  borderColor={useColorModeValue('white', 'gray.800')}
                  shadow="lg"
                />
                
                {/* Avatar Management Overlay */}
                {permissions.manage_team_settings && (
                  <Box
                    position="absolute"
                    bottom={-2}
                    right={-2}
                    bg={useColorModeValue('blackAlpha.600', 'blackAlpha.800')}
                    borderRadius="full"
                    p={1}
                  >
                    <HStack spacing={1}>
                      <Tooltip label="Change Avatar">
                        <IconButton
                          icon={<EditIcon />}
                          size="xs"
                          variant="ghost"
                          colorScheme="white"
                          onClick={() => avatarInputRef.current?.click()}
                          isLoading={isLoadingAvatarBanner}
                          aria-label="Change Avatar"
                        />
                      </Tooltip>
                    </HStack>
                  </Box>
                )}
              </Box>

              {/* Team Details */}
              <VStack align="start" spacing={2} flex={1}>
                <HStack>
                  <Button
                    leftIcon={<BackIcon />}
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/teams')}
                  >
                    {t('common.back')}
                  </Button>
                </HStack>
                
                <Heading size="xl" color={useColorModeValue('gray.800', 'gray.100')}>
                  {team.name}
                </Heading>
                
                {team.description && (
                  <Text color={textColor} fontSize="md">
                    {team.description}
                  </Text>
                )}
              </VStack>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Hidden file inputs */}
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onAvatarUpload(file);
        }}
      />
      <input
        ref={bannerInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onBannerUpload(file);
        }}
      />
    </VStack>
  );
}; 